import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'

interface ProfileSettingsProps {
  profile: any
}

type TextSize = 'small' | 'medium' | 'large' | 'x-large'

const textSizeMap: Record<TextSize, number> = {
  small: 0.875,
  medium: 1.125,
  large: 1.5,
  'x-large': 2.0,
}

const genderOptions: { value: string; label: string }[] = [
  { value: '', label: '—' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]

const ageRangeOptions: { value: string; label: string }[] = [
  { value: '', label: '—' },
  { value: 'under_25', label: 'Under 25' },
  { value: '25_34', label: '25-34' },
  { value: '35_44', label: '35-44' },
  { value: '45_54', label: '45-54' },
  { value: '55_64', label: '55-64' },
  { value: '65_plus', label: '65+' },
]

export default function ProfileSettings({ profile }: ProfileSettingsProps) {
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()
  const queryClient = useQueryClient()
  
  // Helper to get valid text size
  const getValidTextSize = (size: any): TextSize => {
    if (size && textSizeMap[size as TextSize]) {
      return size as TextSize
    }
    return 'medium'
  }
  
  const [displayName, setDisplayName] = useState(profile?.display_name || '')
  const [gender, setGender] = useState(profile?.gender ?? '')
  const [ageRange, setAgeRange] = useState(profile?.age_range ?? '')
  const [homeCity, setHomeCity] = useState(profile?.home_city ?? '')
  const [textSize, setTextSize] = useState<TextSize>(
    getValidTextSize(profile?.a11y_prefs?.textSize)
  )
  const [isSaving, setIsSaving] = useState(false)

  const isModerator =
    profile?.user_type === 'moderator' ||
    profile?.user_type === 'admin' ||
    profile?.role === 'moderator' ||
    profile?.role === 'admin'

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || '')
      setGender(profile.gender ?? '')
      setAgeRange(profile.age_range ?? '')
      setHomeCity(profile.home_city ?? '')
      const validTextSize = getValidTextSize(profile.a11y_prefs?.textSize)
      setTextSize(validTextSize)
      const sizeMultiplier = textSizeMap[validTextSize]
      if (sizeMultiplier) {
        document.documentElement.style.fontSize = `${18 * sizeMultiplier}px`
      }
    }
  }, [profile])

  useEffect(() => {
    // Apply text size when it changes
    const sizeMultiplier = textSizeMap[textSize]
    if (sizeMultiplier) {
      document.documentElement.style.fontSize = `${18 * sizeMultiplier}px`
    }
  }, [textSize])

  const updateProfile = useMutation({
    mutationFn: async (data: {
      display_name: string
      gender: string | null
      age_range: string | null
      home_city: string | null
      a11y_prefs: any
    }) => {
      if (!user) throw new Error('Not authenticated')
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
      setIsSaving(false)
    },
  })

  const handleSave = async () => {
    if (!user) return
    
    setIsSaving(true)
    
    // Update theme if it changed
    const currentTheme = profile?.a11y_prefs?.theme || 'light'
    if (currentTheme !== theme) {
      await setTheme(theme)
    }

    const existingPrefs = profile?.a11y_prefs || {}
    await updateProfile.mutateAsync({
      display_name: displayName,
      gender: gender.trim() || null,
      age_range: ageRange.trim() || null,
      home_city: homeCity.trim() || null,
      a11y_prefs: {
        ...existingPrefs,
        theme: theme,
        textSize: textSize,
      },
    })

    // Apply text size to document
    const sizeMultiplier = textSizeMap[textSize]
    if (sizeMultiplier) {
      document.documentElement.style.fontSize = `${18 * sizeMultiplier}px`
    }
  }

  const handleTextSizeChange = (size: TextSize) => {
    setTextSize(size)
  }

  return (
    <div 
      className="rounded-lg p-6 border-2"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--bg-tertiary)',
        color: 'var(--text-primary)',
      }}
    >
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
        Profile Settings
      </h2>

      <div className="space-y-6">
        {/* Display Name */}
        <div>
          <label 
            htmlFor="display-name" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Display Name
          </label>
          <input
            type="text"
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
            aria-required="true"
          />
        </div>

        {/* Gender */}
        <div>
          <label 
            htmlFor="profile-gender" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Gender
          </label>
          <select
            id="profile-gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            {genderOptions.map((opt) => (
              <option key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Age */}
        <div>
          <label 
            htmlFor="profile-age" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Age
          </label>
          <select
            id="profile-age"
            value={ageRange}
            onChange={(e) => setAgeRange(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            {ageRangeOptions.map((opt) => (
              <option key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Home City */}
        <div>
          <label 
            htmlFor="profile-home-city" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Home City
          </label>
          <input
            type="text"
            id="profile-home-city"
            value={homeCity}
            onChange={(e) => setHomeCity(e.target.value)}
            placeholder="e.g. London"
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Theme Selection */}
        <div>
          <label 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Theme
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer" style={{ color: 'var(--text-primary)' }}>
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === 'light'}
                onChange={() => setTheme('light')}
                className="w-4 h-4"
              />
              <span style={{ color: theme === 'light' ? 'var(--text-link)' : 'var(--text-primary)' }}>
                Light
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" style={{ color: 'var(--text-primary)' }}>
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === 'dark'}
                onChange={() => setTheme('dark')}
                className="w-4 h-4"
              />
              <span style={{ color: theme === 'dark' ? 'var(--text-link)' : 'var(--text-primary)' }}>
                Dark
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" style={{ color: 'var(--text-primary)' }}>
              <input
                type="radio"
                name="theme"
                value="yellow-black"
                checked={theme === 'yellow-black'}
                onChange={() => setTheme('yellow-black')}
                className="w-4 h-4"
              />
              <span style={{ color: theme === 'yellow-black' ? 'var(--text-link)' : 'var(--text-primary)' }}>
                Yellow-Black
              </span>
            </label>
          </div>
        </div>

        {/* Text Size Selection */}
        <div>
          <label 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Text Size
          </label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              type="button"
              onClick={() => handleTextSizeChange('small')}
              className="px-4 py-2 border-2 rounded"
              style={{
                backgroundColor: textSize === 'small' ? 'var(--bg-interactive)' : 'transparent',
                color: textSize === 'small' ? 'var(--text-inverse)' : 'var(--text-primary)',
                borderColor: textSize === 'small' ? 'var(--bg-interactive)' : 'var(--bg-tertiary)',
              }}
              aria-pressed={textSize === 'small'}
            >
              Small
            </button>
            <button
              type="button"
              onClick={() => handleTextSizeChange('medium')}
              className="px-4 py-2 border-2 rounded"
              style={{
                backgroundColor: textSize === 'medium' ? 'var(--bg-interactive)' : 'transparent',
                color: textSize === 'medium' ? 'var(--text-inverse)' : 'var(--text-primary)',
                borderColor: textSize === 'medium' ? 'var(--bg-interactive)' : 'var(--bg-tertiary)',
              }}
              aria-pressed={textSize === 'medium'}
            >
              Medium
            </button>
            <button
              type="button"
              onClick={() => handleTextSizeChange('large')}
              className="px-4 py-2 border-2 rounded"
              style={{
                backgroundColor: textSize === 'large' ? 'var(--bg-interactive)' : 'transparent',
                color: textSize === 'large' ? 'var(--text-inverse)' : 'var(--text-primary)',
                borderColor: textSize === 'large' ? 'var(--bg-interactive)' : 'var(--bg-tertiary)',
              }}
              aria-pressed={textSize === 'large'}
            >
              Large
            </button>
            <button
              type="button"
              onClick={() => handleTextSizeChange('x-large')}
              className="px-4 py-2 border-2 rounded"
              style={{
                backgroundColor: textSize === 'x-large' ? 'var(--bg-interactive)' : 'transparent',
                color: textSize === 'x-large' ? 'var(--text-inverse)' : 'var(--text-primary)',
                borderColor: textSize === 'x-large' ? 'var(--bg-interactive)' : 'var(--bg-tertiary)',
              }}
              aria-pressed={textSize === 'x-large'}
            >
              X-Large
            </button>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Current: x{(textSizeMap[textSize] || textSizeMap.medium).toFixed(3)}
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full px-6 py-3 font-semibold rounded disabled:opacity-50"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
          onMouseEnter={(e) => {
            if (!isSaving) {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
          }}
        >
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  )
}

